import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { SCROLL_PREFERENCES_KEY, WARN_AT_RATIO, getStorageUsage } from '../../../utils/storage';
import { clearAllScrollSpeedPreferences, countScrollSpeedPreferences } from '../../../utils/scrollSpeedPreferences';
import { clearSongCache, getSongCacheUsage } from '../../../utils/songCache';

import './StorageSection.scss';

const formatSize = (characters) => {
  if (characters < 1024) return `${characters} B`;
  if (characters < 1024 * 1024) return `${(characters / 1024).toFixed(0)} KB`;
  return `${(characters / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * What the app is keeping on this device, and a way to let go of it.
 *
 * Each row shows its own size rather than one meter: the two live in different stores with very
 * different limits, and both trim themselves when full. It only warns if localStorage gets tight.
 */
const StorageSection = () => {
  const { t } = useTranslation();
  const [usage, setUsage] = useState(() => getStorageUsage());
  const [songCount, setSongCount] = useState(() => countScrollSpeedPreferences());
  // Kept in IndexedDB, so it is measured asynchronously
  const [songCache, setSongCache] = useState({ count: 0, characters: 0 });

  const refresh = useCallback(() => {
    setUsage(getStorageUsage());
    setSongCount(countScrollSpeedPreferences());
    getSongCacheUsage().then(setSongCache);
  }, []);

  useEffect(refresh, [refresh]);

  if (!usage.available) return null;

  const isTight = usage.ratio >= WARN_AT_RATIO;
  const scrollPreferencesSize = usage.keys.find(({ key }) => key === SCROLL_PREFERENCES_KEY)?.characters || 0;

  const handleClearScrollPreferences = () => {
    clearAllScrollSpeedPreferences();
    refresh();
    toast.success(t('storage.autoScrollCleared'));
  };

  const handleClearSongCache = async () => {
    await clearSongCache();
    refresh();
    toast.success(t('storage.savedSongsCleared'));
  };

  return (
    <div className="settings-section StorageSection">
      <h6 className="settings-section-title">{t('storage.section')}</h6>

      {isTight && (
        <p className="storage-warning">
          {t('storage.runningLow', { used: formatSize(usage.characters), budget: formatSize(usage.budget) })}
        </p>
      )}

      <div className="storage-row">
        <div className="storage-row-text">
          <span className="storage-row-label">{t('storage.autoScroll')}</span>
          <small className="storage-row-description">
            {songCount === 0
              ? t('storage.autoScrollEmpty')
              : t('storage.autoScrollRemembered', { count: songCount, size: formatSize(scrollPreferencesSize) })}
          </small>
        </div>
        <button type="button" className="clear-btn" onClick={handleClearScrollPreferences} disabled={songCount === 0}>
          <TrashIcon />
          {t('common.clear')}
        </button>
      </div>

      <div className="storage-row">
        <div className="storage-row-text">
          <span className="storage-row-label">{t('storage.savedSongs')}</span>
          <small className="storage-row-description">
            {songCache.count === 0
              ? t('storage.savedSongsEmpty')
              : t('storage.savedSongsCount', { count: songCache.count, size: formatSize(songCache.characters) })}
          </small>
        </div>
        <button type="button" className="clear-btn" onClick={handleClearSongCache} disabled={songCache.count === 0}>
          <TrashIcon />
          {t('common.clear')}
        </button>
      </div>
    </div>
  );
};

export default StorageSection;
